import { FileProcessingConcernId, FileProcessingResultStatus } from '@atlas/types'

/** One concern's state as the tile sees it, mirroring the API's `processingResults` entries. */
export interface ConcernState {
	status: FileProcessingResultStatus
	errorMessage?: string
}

/**
 * Human label for a concern, for the badge shown when one fails.
 *
 * Typed against the enum rather than `Record<string, string>`, so adding or
 * renaming a concern fails to compile here instead of silently showing the user
 * a raw `snake_case` id. Two surfaces kept byte-identical private copies of this
 * map before, neither typed, so a rename would have leaked the id twice.
 */
export const CONCERN_NOUNS: Record<FileProcessingConcernId, string> = {
	[FileProcessingConcernId.VIRUS_SCAN]: 'Virus scan',
	[FileProcessingConcernId.THUMBNAIL_GENERATION]: 'Preview',
	[FileProcessingConcernId.AUDIO_TRANSCRIPTION]: 'Transcription',
	[FileProcessingConcernId.CLASSIFICATION]: 'Classification',
	// "Text extraction" is jargon for a reader of a failing tile; what the concern
	// actually delivers is the file being findable by its content.
	[FileProcessingConcernId.TEXT_EXTRACTION]: 'Search indexing',
	// Same noun on purpose: to the person watching the tile these are one step —
	// "is this file searchable yet" — and the fact that a scan needed a second,
	// paid reader is an implementation detail they cannot act on.
	[FileProcessingConcernId.TEXT_EXTRACTION_ESCALATED]: 'Search indexing',
	// And the same noun a third time. An image is made findable by having a model
	// describe it and read the words in it, but from the tile that is still the one
	// question — "is this findable yet" — and which reader answered it is not something
	// the person watching can act on.
	[FileProcessingConcernId.MEDIA_UNDERSTANDING]: 'Search indexing',
	// A fourth time, and the reasoning is unchanged: a video is made findable by reading
	// its speech and the text on its frames, and from the tile that is still the single
	// question of whether the file is findable yet. "Transcription" would be actively
	// misleading here — it names one of the three things a video reading produces, and the
	// one a viewer is least likely to have been looking for.
	[FileProcessingConcernId.VIDEO_UNDERSTANDING]: 'Search indexing',
}

/** {@link CONCERN_NOUNS} with a fallback, for an id the registry gained after this build. */
export const concernNoun = (id: string): string => CONCERN_NOUNS[id as FileProcessingConcernId] ?? id

/**
 * Where the bar sits once every byte is sent but confirm has not answered.
 *
 * Confirm is a single short request with no progress to report, so it gets a
 * small fixed slice rather than a span: the bar advances to 95% and finishes the
 * moment the server accepts the file.
 */
const UPLOAD_SHARE = 95

/** What the tile's single bar should render, already resolved to one state. */
export interface UploadProgressView {
	percent: number
	isIndeterminate: boolean
	label: string
}

const OUTSTANDING = new Set<FileProcessingResultStatus>([
	FileProcessingResultStatus.PENDING,
	FileProcessingResultStatus.RUNNING,
])

/** Concern ids still waiting to finish, in the order the map presents them. */
export function outstandingConcerns(concerns: Record<string, ConcernState>): string[] {
	return Object.keys(concerns).filter((id) => OUTSTANDING.has(concerns[id].status))
}

/** Concern ids that failed, each of which gets its own badge and retry affordance. */
export function failedConcerns(concerns: Record<string, ConcernState>): string[] {
	return Object.keys(concerns).filter((id) => concerns[id].status === FileProcessingResultStatus.FAILED)
}

/**
 * Concern ids that never ran because something they depend on failed.
 *
 * Deliberately NOT folded into {@link failedConcerns}: that list drives the
 * retry affordance, and retrying a skipped concern on its own would just skip
 * it again — the dependency is what has to be retried. These get a badge so the
 * state is visible, without a button that cannot work.
 */
export function skippedConcerns(concerns: Record<string, ConcernState>): string[] {
	return Object.keys(concerns).filter((id) => concerns[id].status === FileProcessingResultStatus.SKIPPED)
}

/**
 * The tile's bar: a determinate 0→100 across the S3 transfer and confirm, and
 * nothing after.
 *
 * The bar deliberately stops at confirm rather than spanning the server's
 * post-upload concerns. Two reasons, both learned from the version that did:
 *
 * 1. **Determinate motion is the whole signal.** The concern span reported a
 *    percentage but rendered `isIndeterminate`, so the computed value was never
 *    drawn as a width — against the observed ~8s delivery latency the user
 *    watched a segment sweep for eight seconds while conveying no advancement.
 *    A bar that moves says "progressing"; one that loops says "alive" at best.
 *
 * 2. **The user's goal is met at confirm.** Virus scanning, thumbnails and
 *    classification are our plumbing; the file is durably stored and attached
 *    once the server accepts it. Concerns still run, and a failure still
 *    surfaces — as a badge on the asset itself, not by withholding completion.
 */
export function deriveUploadProgress(input: {
	uploadProgress: number
	isConfirming: boolean
	isConfirmed: boolean
	concerns: Record<string, ConcernState>
}): UploadProgressView {
	const { uploadProgress, isConfirming, isConfirmed, concerns } = input

	if (isConfirmed) {
		// Terminal either way: the bytes are stored. A concern that failed is the
		// card's business to report, not a reason to hold the bar short of 100.
		// Skipped counts toward the caveat too — some of the file's processing did
		// not happen, which "Uploaded" alone would not convey.
		const anyUnsuccessful = failedConcerns(concerns).length + skippedConcerns(concerns).length > 0
		return { percent: 100, isIndeterminate: false, label: anyUnsuccessful ? 'Uploaded with errors' : 'Uploaded' }
	}

	if (isConfirming) {
		return { percent: UPLOAD_SHARE, isIndeterminate: false, label: 'Finalizing…' }
	}

	const percent = Math.round((Math.min(100, Math.max(0, uploadProgress)) / 100) * UPLOAD_SHARE)
	return { percent, isIndeterminate: false, label: 'Uploading…' }
}
